import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Search, TrendingUp, Users, User, X, Download } from "lucide-react";
import "./SourcingModal.css";

import { fetchCandidatsRequest } from "../../../../redux/actions/candidatsActions";
import {
    fetchCandidaturesByDemandeRequest,
    createCandidatureRequest,
} from "../../../../redux/actions/candidaturesActions";
import { fetchEtablissementsRequest } from "../../../../redux/actions/etablissementsActions";
import { API_BASE_URL, getBlobRequest } from "../../../../../api";

const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

const NIVEAU_ETUDES_LABELS = {
    DIPLOME_BAC_2: "Diplôme (Bac + 2)",
    LICENCE_BAC_3: "Licence (Bac + 3)",
    MASTER_BAC_5: "Master (Bac + 5)",
    INGENIEUR_BAC_5: "Ingénieur (Bac + 5)",
    DOCTORAT_ET_PLUS: "Doctorat et plus",
};

const TYPE_CANDIDATURE_OPTIONS = [
    { value: "REPONSE_OFFRE", label: "Par poste" },
    { value: "CANDIDATURE_SPONTANEE", label: "Candidature spontanée" },
    { value: "AUTRE", label: "Autre" },
];

const SOURCE_OPTIONS = [
    { value: "SITES_CARRIERE", label: "Sites carrière" },
    { value: "CHASSEURS_TETE", label: "Chasseurs de tête" },
    { value: "PARTENAIRES_ACADEMIQUES", label: "Partenaires académiques" },
    { value: "COOPTATION_RECOMMENDATION", label: "Cooptation / recommendation" },
    { value: "AUTRE", label: "Autre" },
];

const labelFromOptions = (options, value, fallback = "—") => {
    if (!value) return fallback;
    const found = options.find((o) => String(o.value) === String(value));
    return found?.label || value;
};

const normalizeCandidat = (c) => {
    const nom = c?.nomCandidat ?? c?.nom ?? "";
    const prenom = c?.prenomCandidat ?? c?.prenom ?? "";
    const email = c?.emailCandidat ?? c?.email ?? "";
    const niveauEtude = c?.niveauxEtudes ?? c?.niveauEtude ?? c?.niveauEtudes ?? "";
    const anneExperience = c?.anneExperience ?? c?.anneesExperience ?? "";
    const diplomeObtenu = c?.diplome ?? c?.diplomeObtenu ?? "";
    const etablissementId = c?.etablissementId ?? c?.etablissement?.id ?? null;
    const autreEtablissement = c?.autreEtablissement ?? "";
    const typeCandidature = c?.typeCandidature ?? "";
    const source = c?.source ?? "";
    const cv = c?.cvDocument ?? c?.cv ?? "";
    const dateAjout = c?.createdAt ?? c?.dateAjout ?? c?.createdDate ?? null;

    return {
        raw: c,
        id: c?.id,
        nomCandidat: nom,
        prenomCandidat: prenom,
        emailCandidat: email,
        niveauEtude,
        anneExperience,
        diplomeObtenu,
        etablissementId,
        autreEtablissement,
        typeCandidature,
        source,
        cv,
        dateAjout,
    };
};

const labelNiveauEtude = (value) => {
    if (!value) return "—";
    return NIVEAU_ETUDES_LABELS[value] || value;
};

const parseExp = (exp) => {
    if (!exp) return 0;
    const v = String(exp).toLowerCase();
    if (v.includes("< 1")) return 0;
    if (v.includes("> 10")) return 10;
    if (v.includes("> 5")) return 5;
    if (v.includes("> 2")) return 2;
    if (v.includes("> 1")) return 1;
    return 0;
};

export default function SourcingModal({ open, onClose, demande }) {
    const dispatch = useDispatch();

    const candidatsState = useSelector((state) => state.candidats || {});
    const candidaturesState = useSelector((state) => state.candidatures || {});
    const etablissementsState = useSelector((state) => state.etablissements || {});

    const candidatsRaw =
        candidatsState.candidats || candidatsState.items || candidatsState.data || [];
    const etablissements = etablissementsState.etablissements || [];
    const candidaturesDemande = candidaturesState.candidatures || [];

    const safeDemande = demande || {};

    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [filters, setFilters] = useState({
        fullname: "",
        source: "",
        diplomeObtenu: "",
        niveauEtude: "",
    });

    useEffect(() => {
        if (!open) return;

        dispatch(fetchCandidatsRequest());
        dispatch(fetchEtablissementsRequest());

        if (safeDemande?.id) {
            dispatch(fetchCandidaturesByDemandeRequest(safeDemande.id));
        }

        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [dispatch, open, safeDemande?.id]);

    useEffect(() => {
        if (!open) {
            setSelectedCandidates([]);
            setFilters({
                fullname: "",
                source: "",
                diplomeObtenu: "",
                niveauEtude: "",
            });
        }
    }, [open]);

    const candidats = useMemo(() => {
        return Array.isArray(candidatsRaw) ? candidatsRaw.map(normalizeCandidat) : [];
    }, [candidatsRaw]);

    const alreadyLinkedIds = useMemo(() => {
        return new Set(
            (candidaturesDemande || [])
                .map((c) => Number(c.candidatId))
                .filter((id) => !Number.isNaN(id))
        );
    }, [candidaturesDemande]);

    const getEtablissementLabel = (candidat) => {
        if (candidat?.autreEtablissement?.trim()) return candidat.autreEtablissement.trim();

        const et = (etablissements || []).find(
            (e) => Number(e.id) === Number(candidat?.etablissementId)
        );

        return et ? et.abbreviation || et.nom || "—" : "—";
    };

    const getDemandePosteLabel = () => {
        if (safeDemande?.autrePoste?.trim()) return safeDemande.autrePoste.trim();
        return safeDemande?.posteNom || safeDemande?.poste || "—";
    };

    const getDemandeEtablissementLabel = () => {
        if (safeDemande?.autreEtablissement?.trim()) return safeDemande.autreEtablissement.trim();
        return safeDemande?.etablissementNom || safeDemande?.etablissement || "—";
    };

    const calculateMatchScore = (candidat, demandeArg) => {
        let score = 0;

        const niveauCandidat = labelNiveauEtude(candidat.niveauEtude);
        const niveauDemande = demandeArg?.niveauEtude || demandeArg?.niveauxEtudes || "";
        score += niveauCandidat === niveauDemande ? 30 : 15;

        const expCandidat = parseExp(candidat.anneExperience);
        const expDemande = parseExp(demandeArg?.anneesExperience);
        if (expCandidat >= expDemande + 2) score += 35;
        else if (expCandidat >= expDemande) score += 30;
        else score += 15;

        const etabCandidat = getEtablissementLabel(candidat);
        const etabDemande = getDemandeEtablissementLabel();
        score += etabCandidat === etabDemande ? 35 : 15;

        return Math.min(90, Math.round(score * (0.9 + Math.random() * 0.2)));
    };

    const getScoreColor = (score) => {
        if (score >= 85) return "#4CAF50";
        if (score >= 70) return "#2196F3";
        if (score >= 50) return "#ff9800";
        return "#f44336";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const handleDownloadCV = async (candidat) => {
        try {
            if (!candidat?.cv) {
                showSwal({
                    icon: "warning",
                    text: "Aucun CV disponible pour ce candidat",
                    timer: 1800,
                    showConfirmButton: false,
                });
                return;
            }

            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const selectedCompanyId = sessionStorage.getItem("selectedCompanyId");

            const url = `${API_BASE_URL}/api/candidat-theque/cv/${encodeURIComponent(candidat.cv)}`;

            const response = await getBlobRequest({
                url,
                token,
                extraHeaders: {
                    uid,
                    "X-Tenant-Id": selectedCompanyId,
                },
            });

            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = candidat.cv;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Erreur téléchargement CV:", error);
            showSwal({
                icon: "error",
                text: "Impossible de télécharger le CV.",
                timer: 2200,
                showConfirmButton: false,
            });
        }
    };

    const aiRecommendations = useMemo(() => {
        return candidats
            .filter((c) => !alreadyLinkedIds.has(Number(c.id)))
            .map((c) => ({
                ...c,
                matchScore: calculateMatchScore(c, safeDemande),
            }))
            .sort((a, b) => b.matchScore - a.matchScore)
            .slice(0, 3);
    }, [candidats, alreadyLinkedIds, safeDemande]);

    const sourceOptions = useMemo(() => {
        const values = [
            ...new Set(
                candidats
                    .map((c) => labelFromOptions(SOURCE_OPTIONS, c.source, ""))
                    .filter(Boolean)
            ),
        ];
        return values.sort((a, b) => a.localeCompare(b));
    }, [candidats]);

    const niveauOptions = useMemo(() => {
        const values = [...new Set(candidats.map((c) => labelNiveauEtude(c.niveauEtude)).filter(Boolean))];
        return values.sort((a, b) => a.localeCompare(b));
    }, [candidats]);

    const filteredCandidats = useMemo(() => {
        return candidats.filter((c) => {
            if (filters.fullname) {
                const fullName = `${c.nomCandidat} ${c.prenomCandidat}`.toLowerCase();
                if (!fullName.includes(filters.fullname.toLowerCase())) return false;
            }

            if (
                filters.source &&
                labelFromOptions(SOURCE_OPTIONS, c.source, "") !== filters.source
            ) {
                return false;
            }

            if (
                filters.diplomeObtenu &&
                !String(c.diplomeObtenu || "")
                    .toLowerCase()
                    .includes(filters.diplomeObtenu.toLowerCase())
            ) {
                return false;
            }

            if (filters.niveauEtude && labelNiveauEtude(c.niveauEtude) !== filters.niveauEtude) {
                return false;
            }

            return true;
        });
    }, [candidats, filters]);

    const toggleSelection = (candidat) => {
        const candidateId = Number(candidat.id);
        if (alreadyLinkedIds.has(candidateId)) return;

        const isSelected = selectedCandidates.some((c) => Number(c.id) === candidateId);

        setSelectedCandidates((prev) =>
            isSelected
                ? prev.filter((c) => Number(c.id) !== candidateId)
                : [...prev, candidat]
        );
    };

    const handleSave = () => {
        if (!safeDemande?.id) {
            showSwal({
                icon: "warning",
                text: "Aucune demande sélectionnée",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        if (selectedCandidates.length === 0) {
            showSwal({
                icon: "warning",
                text: "Veuillez sélectionner au moins un candidat",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        selectedCandidates.forEach((candidat) => {
            dispatch(
                createCandidatureRequest({
                    demandeId: Number(safeDemande.id),
                    candidatId: Number(candidat.id),
                    statut: "en attente",
                })
            );
        });

        showSwal({
            icon: "success",
            text: `${selectedCandidates.length} candidat(s) proposé(s) pour le poste "${getDemandePosteLabel()}"`,
            timer: 1800,
            showConfirmButton: false,
        });

        setSelectedCandidates([]);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="smModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="smModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="smModal__header">
                    <div className="smModal__title">
                        <Users size={18} />
                        Sourcing des candidats — {getDemandePosteLabel()}
                    </div>
                    <button className="smIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={18} />
                    </button>
                </div>

                <div className="smModal__body">
                    <div className="smSection">
                        <div className="smSection__title">
                            <Search size={16} />
                            Filtres de recherche
                        </div>

                        <div className="smGrid">
                            <div className="smField">
                                <label>Nom Complet</label>
                                <input
                                    type="text"
                                    value={filters.fullname}
                                    onChange={(e) => setFilters({ ...filters, fullname: e.target.value })}
                                    placeholder="Rechercher..."
                                />
                            </div>

                            <div className="smField">
                                <label>Source</label>
                                <select
                                    value={filters.source}
                                    onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                                >
                                    <option value="">Toutes</option>
                                    {sourceOptions.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="smField">
                                <label>Diplôme</label>
                                <input
                                    type="text"
                                    value={filters.diplomeObtenu}
                                    onChange={(e) =>
                                        setFilters({ ...filters, diplomeObtenu: e.target.value })
                                    }
                                    placeholder="Ex: Master..."
                                />
                            </div>

                            <div className="smField">
                                <label>Niveau d'étude</label>
                                <select
                                    value={filters.niveauEtude}
                                    onChange={(e) => setFilters({ ...filters, niveauEtude: e.target.value })}
                                >
                                    <option value="">Tous</option>
                                    {niveauOptions.map((n) => (
                                        <option key={n} value={n}>
                                            {n}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {aiRecommendations.length > 0 && (
                        <div className="smSection">
                            <div className="smSection__title">
                                <TrendingUp size={16} />
                                Recommandations IA (Top 3)
                            </div>

                            <div className="smRecGrid">
                                {aiRecommendations.map((candidat) => {
                                    const isSelected = selectedCandidates.some(
                                        (c) => Number(c.id) === Number(candidat.id)
                                    );
                                    const alreadyLinked = alreadyLinkedIds.has(Number(candidat.id));

                                    return (
                                        <div
                                            key={candidat.id}
                                            className="smRecCard"
                                            style={{ borderLeftColor: getScoreColor(candidat.matchScore) }}
                                        >
                                            <div className="smRecTop">
                                                <div className="smRecName">
                                                    {candidat.nomCandidat} {candidat.prenomCandidat}
                                                </div>
                                                <div
                                                    className="smScore"
                                                    style={{ background: getScoreColor(candidat.matchScore) }}
                                                >
                                                    {candidat.matchScore}%
                                                </div>
                                            </div>

                                            <div className="smRecMeta">
                                                <div>📖 {candidat.diplomeObtenu || "—"}</div>
                                                <div>🎓 {labelNiveauEtude(candidat.niveauEtude)}</div>
                                                <div>💼 {candidat.anneExperience || "—"}</div>
                                                <div>🏛️ {getEtablissementLabel(candidat)}</div>
                                                <div>📄 {labelFromOptions(TYPE_CANDIDATURE_OPTIONS, candidat.typeCandidature)}</div>
                                                <div>📌 {labelFromOptions(SOURCE_OPTIONS, candidat.source)}</div>
                                            </div>

                                            <div className="smRecActions">
                                                <button
                                                    className={`smBtn ${
                                                        alreadyLinked
                                                            ? "smBtn--muted"
                                                            : isSelected
                                                                ? "smBtn--muted"
                                                                : "smBtn--primary"
                                                    }`}
                                                    onClick={() => toggleSelection(candidat)}
                                                    type="button"
                                                    disabled={alreadyLinked}
                                                >
                                                    {alreadyLinked
                                                        ? "Déjà proposé"
                                                        : isSelected
                                                            ? "✓ Sélectionné"
                                                            : "Sélectionner"}
                                                </button>

                                                <button
                                                    className="smBtn smBtn--cv"
                                                    onClick={() => handleDownloadCV(candidat)}
                                                    type="button"
                                                    title={`Télécharger le CV de ${candidat.prenomCandidat}`}
                                                >
                                                    <Download size={14} />
                                                    CV
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="smSection">
                        <div className="smSection__title">
                            <User size={16} />
                            Tous les candidats ({filteredCandidats.length})
                        </div>

                        <div className="smTableWrap">
                            <table className="smTable">
                                <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input type="checkbox" disabled />
                                    </th>
                                    <th style={{ width: 56 }}>CV</th>
                                    <th>Nom complet</th>
                                    <th>Expérience</th>
                                    <th>Candidature</th>
                                    <th>Source</th>
                                    <th>Date d'ajout</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredCandidats.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="smEmptyCell">
                                            Aucun candidat trouvé
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCandidats.map((candidat) => {
                                        const isSelected = selectedCandidates.some(
                                            (c) => Number(c.id) === Number(candidat.id)
                                        );
                                        const alreadyLinked = alreadyLinkedIds.has(Number(candidat.id));

                                        return (
                                            <tr
                                                key={candidat.id}
                                                className={isSelected ? "smRow--selected" : ""}
                                            >
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelection(candidat)}
                                                        disabled={alreadyLinked}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        className="smCvBtn"
                                                        onClick={() => handleDownloadCV(candidat)}
                                                        title={`Télécharger ${candidat.cv || "CV"}`}
                                                        type="button"
                                                    >
                                                        <Download size={15} />
                                                    </button>
                                                </td>
                                                <td className="smStrong">
                                                    {candidat.nomCandidat} {candidat.prenomCandidat}
                                                    {alreadyLinked && (
                                                        <div className="smMuted" style={{ fontSize: 12 }}>
                                                            Déjà proposé à cette demande
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="smMuted">
                                                    {candidat.anneExperience || "—"}
                                                </td>
                                                <td className="smMuted">
                                                    {labelFromOptions(TYPE_CANDIDATURE_OPTIONS, candidat.typeCandidature)}
                                                </td>
                                                <td className="smMuted">
                                                    {labelFromOptions(SOURCE_OPTIONS, candidat.source)}
                                                </td>
                                                <td className="smMuted smDateCell">
                                                    {formatDate(candidat.dateAjout)}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="smModal__footer">
                    <div className="smFooterLeft">
                        {selectedCandidates.length} candidat(s) sélectionné(s)
                    </div>

                    <button className="smBtn smBtn--ghost" onClick={onClose} type="button">
                        Annuler
                    </button>

                    <button
                        className="smBtn smBtn--primary"
                        onClick={handleSave}
                        disabled={selectedCandidates.length === 0}
                        type="button"
                    >
                        Sélectionner
                    </button>
                </div>
            </div>
        </div>
    );
}